import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios"
import type {
  Division,
  SidebarCounts,
  ContactSearchResult,
  SearchParams,
} from "../../types"
import { errorLog } from "../errorLog"
import { keys } from "../../keys"

// Error logging state - can be enabled/disabled based on user settings
// Initialize from localStorage so it persists across page reloads
const ERROR_LOGGING_KEY = "error_logging_enabled"
let errorLoggingEnabled = localStorage.getItem(ERROR_LOGGING_KEY) === "true"

export const setErrorLoggingEnabled = (enabled: boolean) => {
  errorLoggingEnabled = enabled
  // Persist to localStorage so it's available immediately on next page load
  if (enabled) {
    localStorage.setItem(ERROR_LOGGING_KEY, "true")
  } else {
    localStorage.removeItem(ERROR_LOGGING_KEY)
  }
}

export const isErrorLoggingEnabled = () => errorLoggingEnabled

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: keys.API_URL,
  timeout: 30000,
  withCredentials: true, // Send cookies for Passport session authentication
  headers: {
    "Content-Type": "application/json",
  },
})

// Track request start times for error logging
const requestStartTimes = new Map<string, number>()

// Request interceptor - add system API key for authorization
apiClient.interceptors.request.use(
  (config) => {
    // Use system API key like the original app (server uses Passport sessions + this key)
    config.headers.Authorization = `Bearer ${keys.SYSTEM_API_KEY}`

    // Track start time for error logging
    if (errorLoggingEnabled) {
      const requestId = `${config.method}-${config.url}-${Date.now()}`
      config.headers["X-Request-Id"] = requestId
      requestStartTimes.set(requestId, Date.now())
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Means Test staleness/refresh types
export interface MeansTestStaleness {
  isStale: boolean
  neverCalculated?: boolean
  lastCalculatedAt: number | null
  changedSources: Array<{ form: string; label: string; lastModified: number; calculatedFrom: number }>
  unchangedSources: Array<{ form: string; label: string; lastModified: number }>
}

export interface MeansTestPreviewField {
  formType: string
  path: string
  label: string
  currentValue: number | string | boolean | null
  newValue: number | string | boolean
  status: "changed" | "new" | "overridden" | "unchanged"
  isOverridden: boolean
}

export interface MeansTestPreviewRefresh {
  fields: MeansTestPreviewField[]
  summary: { total: number; changed: number; new: number; overridden: number; unchanged: number }
}

// Activity tracking - store network logs for action detection
interface ActivityNetworkLog {
  timestamp: string
  type: "axios"
  url: string
  method: string
  status: number
  response?: string
}
const activityNetworkLogs: ActivityNetworkLog[] = []
const MAX_ACTIVITY_RESPONSE_LENGTH = 1500 // Enough to capture type field in notes response

export function getActivityNetworkLogs(): ActivityNetworkLog[] {
  const logs = [...activityNetworkLogs]
  activityNetworkLogs.length = 0
  return logs
}

// Diagnostic network logs - rolling buffer for ticket diagnostics (not drained)
interface DiagnosticNetworkLog {
  timestamp: string
  url: string
  method: string
  status: number
  failed: boolean
  error_message?: string
  duration_ms?: number
  response_body?: string
  request_body?: string
}
const diagnosticNetworkLogs: DiagnosticNetworkLog[] = []
const MAX_DIAGNOSTIC_LOGS = 100
const MAX_DIAGNOSTIC_ERROR_LENGTH = 2000
const MAX_DIAGNOSTIC_RESPONSE_LENGTH = 50000
const MAX_DIAGNOSTIC_REQUEST_LENGTH = 20000

function pushDiagnosticLog(entry: DiagnosticNetworkLog) {
  diagnosticNetworkLogs.push(entry)
  if (diagnosticNetworkLogs.length > MAX_DIAGNOSTIC_LOGS) {
    diagnosticNetworkLogs.shift()
  }
}

export function getDiagnosticNetworkLogs(): DiagnosticNetworkLog[] {
  return [...diagnosticNetworkLogs]
}

// Response interceptor - handle errors and log requests
apiClient.interceptors.response.use(
  (response) => {
    // Log for activity tracking
    try {
      const url = response.config.url || ""
      const method = (response.config.method || "GET").toUpperCase()
      const responseStr = typeof response.data === "string"
        ? response.data
        : JSON.stringify(response.data)
      activityNetworkLogs.push({
        timestamp: new Date().toISOString(),
        type: "axios",
        url,
        method,
        status: response.status,
        response: responseStr.slice(0, MAX_ACTIVITY_RESPONSE_LENGTH),
      })
      // Calculate duration from request start time
      const requestId = response.config.headers?.["X-Request-Id"] as string | undefined
      let durationMs: number | undefined
      if (requestId && requestStartTimes.has(requestId)) {
        durationMs = Date.now() - requestStartTimes.get(requestId)!
      }
      // Capture request/response bodies for all requests
      let responseBody: string | undefined
      try {
        responseBody = responseStr.slice(0, MAX_DIAGNOSTIC_RESPONSE_LENGTH)
      } catch { /* ignore */ }
      let requestBody: string | undefined
      if (["POST", "PATCH", "PUT", "DELETE"].includes(method) && response.config.data) {
        try {
          const reqData = typeof response.config.data === "string"
            ? response.config.data
            : JSON.stringify(response.config.data)
          requestBody = reqData.slice(0, MAX_DIAGNOSTIC_REQUEST_LENGTH)
        } catch { /* ignore */ }
      }
      pushDiagnosticLog({
        timestamp: new Date().toISOString(),
        url,
        method,
        status: response.status,
        failed: false,
        duration_ms: durationMs,
        response_body: responseBody,
        request_body: requestBody,
      })
    } catch {
      // Ignore logging errors
    }

    // Log successful request if error logging is enabled
    if (errorLoggingEnabled) {
      const requestId = response.config.headers?.["X-Request-Id"] as string
      const start = requestStartTimes.get(requestId) || Date.now()
      const end = Date.now()
      requestStartTimes.delete(requestId)
      errorLog.store(response, start, end)
    }

    // Check for API-level failure (success: false with 200 status)
    // The backend returns { success: false, message: [...] } for failures
    const data = response.data as { success?: boolean; message?: string[] }
    if (data && data.success === false) {
      const rawMsg = data.message?.[0]
      const msg = typeof rawMsg === "string" ? rawMsg : rawMsg ? JSON.stringify(rawMsg) : "Request failed"
      // Update last diagnostic log entry to reflect failure
      if (diagnosticNetworkLogs.length > 0) {
        const last = diagnosticNetworkLogs[diagnosticNetworkLogs.length - 1]
        if (last.url === (response.config.url || "")) {
          last.failed = true
          last.error_message = msg.slice(0, MAX_DIAGNOSTIC_ERROR_LENGTH)
          // Capture response body for failed requests
          try {
            last.response_body = JSON.stringify(response.data).slice(0, MAX_DIAGNOSTIC_RESPONSE_LENGTH)
          } catch { /* ignore */ }
          // Capture request body for failed mutations
          const method = (response.config.method || "").toUpperCase()
          if (["POST", "PATCH", "PUT", "DELETE"].includes(method) && response.config.data) {
            try {
              const reqData = typeof response.config.data === "string"
                ? response.config.data
                : JSON.stringify(response.config.data)
              last.request_body = reqData.slice(0, MAX_DIAGNOSTIC_REQUEST_LENGTH)
            } catch { /* ignore */ }
          }
        }
      }
      const error = new Error(msg) as Error & { response: typeof response }
      error.response = response
      return Promise.reject(error)
    }
    return response
  },
  (error: AxiosError) => {
    // Log error if error logging is enabled
    if (errorLoggingEnabled) {
      const requestId = error.config?.headers?.["X-Request-Id"] as string
      const start = requestStartTimes.get(requestId) || Date.now()
      const end = Date.now()
      requestStartTimes.delete(requestId)
      errorLog.store(error, start, end)
    }

    // Log to diagnostic buffer for ticket diagnostics
    try {
      const method = (error.config?.method || "GET").toUpperCase()
      const entry: DiagnosticNetworkLog = {
        timestamp: new Date().toISOString(),
        url: error.config?.url || "",
        method,
        status: error.response?.status || 0,
        failed: true,
        error_message: (error.message || "").slice(0, MAX_DIAGNOSTIC_ERROR_LENGTH),
      }
      // Capture duration
      const requestId = error.config?.headers?.["X-Request-Id"] as string | undefined
      if (requestId && requestStartTimes.has(requestId)) {
        entry.duration_ms = Date.now() - requestStartTimes.get(requestId)!
      }
      // Capture response body for failed requests
      if (error.response?.data) {
        try {
          entry.response_body = JSON.stringify(error.response.data).slice(0, MAX_DIAGNOSTIC_RESPONSE_LENGTH)
        } catch { /* ignore */ }
      }
      // Capture request body for failed mutations
      if (["POST", "PATCH", "PUT", "DELETE"].includes(method) && error.config?.data) {
        try {
          const reqData = typeof error.config.data === "string"
            ? error.config.data
            : JSON.stringify(error.config.data)
          entry.request_body = reqData.slice(0, MAX_DIAGNOSTIC_REQUEST_LENGTH)
        } catch { /* ignore */ }
      }
      pushDiagnosticLog(entry)
    } catch {
      // Ignore logging errors
    }

    return Promise.reject(error)
  }
)

// Generic request function
async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.request<T>(config)
  return response.data
}

// API endpoints organized by domain
export const api = {
  // Auth
  auth: {
    login: (email: string, password: string) =>
      request<{ token: string; user: any }>({
        method: "POST",
        url: "/v1/auth/login",
        data: { email, password },
      }),
    logout: () =>
      request<void>({
        method: "POST",
        url: "/v1/auth/logout",
      }),
    me: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/auth/me",
      }),
    viewingUser: () =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: "/v1/auth/viewing_user",
      }),
    password: {
      update: (data: { user_id: string; password: string; passwordConfirm: string }) =>
        request<{ success: boolean; message?: string }>({
          method: "POST",
          url: "/v1/auth/password/update",
          data,
        }),
    },
    mfa: {
      send: (params: { _id: string; company: string; type: "phone" | "email" }) =>
        request<{ success: boolean }>({
          method: "GET",
          url: `/v1/auth/mfa`,
          params,
        }),
      set: (data: { user: string; type: "phone" | "email"; code: string }) =>
        request<{ success: boolean }>({
          method: "POST",
          url: "/v1/auth/mfa/set",
          data,
        }),
    },
  },

  // Matters
  matters: {
    search: (params: SearchParams) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/matters/search",
        data: params,
      }),
    searchList: (params?: { page?: number; limit?: number; search?: string; status?: string }) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/matters/search",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}`,
      }),
    overview: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/overview`,
      }),
    overviewCommunication: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/overview_communication`,
      }),
    related: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/matters/${id}/related`,
      }),
    partyContacts: (id: string, params?: { search?: string }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "GET",
        url: `/v1/matters/${id}/party_contacts`,
        params,
      }),
    communicationAnalytics: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/communication_analytics`,
      }),
    bookmarkAdd: (data: { matter: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matters/bookmark/add",
        data,
      }),
    bookmarkRemove: (data: { matter: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matters/bookmark/remove",
        data,
      }),
    watch: (data: { matter: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: "/v1/matters/watch",
        data,
      }),
    unwatch: (data: { matter: string }) =>
      request<{ success: boolean; data: any }>({
        method: "DELETE",
        url: "/v1/matters/watch",
        data,
      }),
    mute: (data: { matter: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: "/v1/matters/mute",
        data,
      }),
    unmute: (data: { matter: string }) =>
      request<{ success: boolean; data: any }>({
        method: "DELETE",
        url: "/v1/matters/mute",
        data,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/matters",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/matters/${id}`,
        data,
      }),
    linkAdd: (data: { matter: string; workflow_link: string; link: string }) =>
      request<{ data: { links: Array<{ workflow_link: string; link: string }> } }>({
        method: "PATCH",
        url: "/v1/matters/links",
        data,
      }),
    updateBillingRates: (id: string, data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/matters/${id}/billing_rates`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matters/${id}`,
      }),
    restore: (id: string) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/restore`,
      }),
    moveStep: (id: string, data: { workflow_step: string; jump?: boolean; check?: boolean; matter_closing_reason?: string; matter_closing_reason_notes?: string }) =>
      request<{ success: boolean; message?: string[]; data?: { err?: string | null; tasks?: any[]; moving_tasks?: any[] } }>({
        method: "PATCH",
        url: `/v1/matters/${id}/step`,
        data,
      }),
    // Party management
    partyAdd: (data: { matter: string; workflow_contact: string; contact: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/parties",
        data,
      }),
    partyRemove: (data: { matter: string; workflow_contact: string; contact: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/parties",
        data,
      }),
    // Role management
    roleAdd: (data: { matter: string; workflow_role: string; user: string; note?: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/roles",
        data,
      }),
    roleRemove: (data: { matter: string; workflow_role: string; user: string; note?: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/roles",
        data,
      }),
    // Contact time preferences
    setContactTime: (id: string, data: { contact: string; time: string }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/contact_time`,
        data,
      }),
    // Tag management
    tagAdd: (data: { matter: string; tag: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/tags",
        data,
      }),
    tagRemove: (data: { matter: string; tag: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/tags",
        data,
      }),
    // Location management
    locationAdd: (data: { matter: string; workflow_location: string; location: string; task?: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/locations",
        data,
      }),
    locationRemove: (data: { matter: string; workflow_location: string; location: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/matters/locations",
        data,
      }),
    // Custom date management (matter milestones)
    customDateAdd: (data: { matter: string; custom_date: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/custom_dates",
        data,
      }),
    // Expected date management
    expectedDateAdd: (data: { matter: string; expected_date: string; date: number; task?: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: "/v1/matters/expected_dates",
        data,
      }),
    // Alert management
    alertAdd: (data: { matter: string; body: string }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/matters/alerts",
        data,
      }),
    alertResolve: (data: { matter: string; alert: string }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: "/v1/matters/alerts",
        data,
      }),
    // Billing
    billing: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/matters/${id}/billing`,
      }),
    trust: (id: string, data: { billing_for_trust?: number }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/matters/${id}/trust`,
        data,
      }),
    // Lead sources management
    leadSources: (id: string, data: { lead_sources: Array<{ lead_source: string; lead_source_percent: number }> }) =>
      request<{ success: boolean; data?: any }>({
        method: "PATCH",
        url: `/v1/matters/${id}/lead_sources`,
        data,
      }),
  },

  // Payments
  payments: {
    charge: (data: {
      matter: string
      payment_method?: string
      amount: number
      type: "one_time" | "cash"
      date?: number
      description?: string
      is_trust_movement?: boolean
    }) =>
      request<{ success: boolean; data?: { amount_failed?: number; payments?: any[] }; message?: string[] }>({
        method: "POST",
        url: "/v1/payments",
        baseURL: keys.PROCESSING_URL,
        data,
      }),
    refund: (data: { refund_for: string; matter: string; type: "one_time" | "cash" | "recurring"; payment_method?: string; amount: number }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payments/refund",
        baseURL: keys.PROCESSING_URL,
        data,
      }),
    download: (matterId: string) =>
      apiClient.get(`/v1/payments/${matterId}/download`, { responseType: "blob" }),
  },

  // Payment Methods
  paymentMethods: {
    create: (data: { matter: string; token: string; owner_name: string; payment_processor: string; type: string; last_4: string; zip: string; expires: string; primary_method?: boolean; backup_method?: boolean }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_methods",
        data,
      }),
    update: (id: string, data: { type: "default" | "backup" }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_methods/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/payment_methods/${id}`,
      }),
    hosted: (matterId: string, processor: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/payment_methods/${matterId}/hosted/${processor}`,
      }),
  },

  // Payment Subscriptions (Recurring Payment Plans)
  paymentSubscriptions: {
    create: (data: {
      matter: string
      amount: number
      interval: string
      start_date: string
      payment_method: string
      payment_method_backup?: string
      run_time: "morning" | "night"
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_subscriptions",
        data,
      }),
    update: (
      id: string,
      data: {
        matter: string
        amount: number
        interval: string
        next_run_date: string
        payment_method: string
        payment_method_backup?: string
        run_time: "morning" | "night"
      }
    ) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_subscriptions/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/payment_subscriptions/${id}`,
      }),
    resetPortalChanges: (matterId: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: `/v1/payment_subscriptions/reset_portal_changes/${matterId}`,
      }),
  },

  // Invoices
  invoices: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/invoices/${id}`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/invoices",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/invoices/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/invoices/${id}`,
      }),
    chapter13FeeApp: (id: string) =>
      apiClient.get(`/v1/invoices/${id}/chapter-13-fee-application`, {
        responseType: "arraybuffer",
      }),
    download: (id: string) =>
      apiClient.get(`/v1/invoices/${id}/download`, {
        responseType: "arraybuffer",
      }),
  },

  // Payment Trust Entries
  paymentTrustEntries: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/payment_trust_entries/${matterId}`,
      }),
    create: (data: {
      matter: string
      action_type: "payment" | "received"
      amount: number
      date: string
      payment_method: string
      reason: string
      party?: string
      party_address?: string
      payment?: string
      memo?: string
      check_number?: string
      check_series?: string
      check_date?: string
      check_from?: string
      transaction_reference?: string
      to_account_number?: string
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/payment_trust_entries",
        data,
      }),
    reverse: (id: string, data?: { description?: string }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/payment_trust_entries/${id}/reverse`,
        data,
      }),
  },

  // Workflow Contacts (parties assigned to matters)
  workflowContacts: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_contacts`,
        params: { workflow: workflowId },
      }),
  },

  // Workflow Roles (staff roles for matters)
  workflowRoles: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_roles`,
        params: { workflow: workflowId },
      }),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/workflow_roles/search",
        data,
      }),
  },

  // Workflow Locations (locations for matters)
  workflowLocations: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_locations`,
        params: { workflow: workflowId },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflow_locations/${id}`,
      }),
  },

  // Contacts
  contacts: {
    search: (params: SearchParams) =>
      request<{ data: { documents: ContactSearchResult[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/contacts/search",
        data: params,
      }),
    searchList: (params?: { page?: number; limit?: number; search?: string }) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/contacts/search",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/contacts/${id}`,
      }),
    overview: (id: string) =>
      request<{ data: { contact: any; matters: any[]; lastNote: any } }>({
        method: "GET",
        url: `/v1/contacts/${id}/overview`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/contacts",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}`,
        data,
      }),
    divisions: (id: string, data: { divisions: any[] }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}/divisions`,
        data,
      }),
    password: (id: string, data: { password: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/contacts/${id}/password`,
        data,
      }),
    getSocialSecurityNumber: (contactId: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/social_security_numbers/${contactId}`,
      }),
  },

  // Page Histories
  pageHistories: {
    create: (pathname: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/page_histories",
        data: { pathname },
      }),
  },

  // Notes
  notes: {
    find: (params: { contact?: string; matter?: string; type?: string; limit?: number; skip?: number }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/notes",
        params,
      }),
    create: (data: { value: string; type?: string; contact?: string; matter?: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/notes",
        data,
      }),
    update: (id: string, data: { value: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/notes/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/notes/${id}`,
      }),
  },

  // Contact Types
  contactTypes: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/contact_types",
      }),
  },

  // Social Security Numbers
  socialSecurityNumbers: {
    findByContact: (contactId: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/social_security_numbers/${contactId}`,
      }),
    save: (contactId: string, data: { social_security_number: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/social_security_numbers/${contactId}`,
        data,
      }),
  },

  // Tasks
  tasks: {
    search: (data: {
      skip?: number
      limit?: number
      sort?: Record<string, number>
      filter: Record<string, unknown>
    }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/tasks/search",
        data,
      }),
    findByStep: (stepId: string, matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/tasks/by_step/${stepId}/${matterId}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/tasks/${id}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/tasks",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/tasks/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/tasks/${id}`,
      }),
    finish: (id: string, data?: any) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/tasks/${id}/finish`,
        data,
      }),
    recipients: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/tasks/${id}/recipients`,
      }),
  },

  // Users
  users: {
    list: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/users",
      }),
    me: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/users/me",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/users/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/users/search",
        data: params,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/users/${id}`,
        data,
      }),
    divisionOverride: (id: string, data: { division: string; [key: string]: any }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/users/${id}/division_override`,
        data,
      }),
  },

  // Company/Config
  config: {
    company: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/config/company",
      }),
    divisions: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/config/divisions",
      }),
  },

  // Custom Fields
  customFields: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/custom_fields/${id}`,
      }),
    findByDivision: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/custom_fields/${divisionId}/division`,
      }),
    search: (params: SearchParams) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/custom_fields/search",
        data: params,
      }),
  },

  // Custom Dates (matter milestone types for Set Tag/Date feature)
  customDates: {
    find: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/custom_dates",
        params: { workflow: divisionId },
      }),
  },

  // Expected Dates (deadline types for Set Tag/Date feature)
  expectedDates: {
    find: (divisionId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/expected_dates",
        params: { workflow: divisionId },
      }),
  },

  // Custom Views
  customViews: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/custom_views",
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/custom_views/${id}`,
      }),
  },

  // Documents
  documents: {
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number; sort?: Record<string, number> }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/documents/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/documents/${id}`,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/documents/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/documents/${id}`,
      }),
    bulkDelete: (data: { documents: string[] }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/documents/bulk",
        data,
      }),
    pin: (data: { documents: string[]; pinned: boolean }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: "/v1/documents/pin",
        data,
      }),
    // Upload goes to processing service (port 5002)
    upload: (formData: FormData) =>
      axios.post<{ success: boolean; data: any }>(`${keys.PROCESSING_URL}/v1/documents`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${keys.SYSTEM_API_KEY}`,
        },
      }),
    download: (id: string) =>
      request<{ data: string }>({
        method: "GET",
        url: `/v1/documents/${id}/download`,
      }),
    mergePages: (data: { matter: string; name: string; pages: Array<{ document: string; page: number; rotation?: number }> }) =>
      request<{ success: boolean; data?: any; message?: string[]; warnings?: Array<{ document: string; page: number; reason: string }> }>({
        method: "POST",
        url: "/v1/documents/merge-pages",
        data,
      }),
  },

  // Document Annotations
  documentAnnotations: {
    find: (documentId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/document_annotations/${documentId}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/document_annotations",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/document_annotations/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/document_annotations/${id}`,
      }),
    addReply: (id: string, body: string) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/document_annotations/${id}/reply`,
        data: { body },
      }),
    deleteReply: (id: string, replyId: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/document_annotations/${id}/reply/${replyId}`,
      }),
    resolve: (id: string) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/document_annotations/${id}/resolve`,
      }),
    applyRedactions: (documentId: string) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/document_annotations/${documentId}/apply-redactions`,
      }),
  },

  // Matter Document Uploads (Categories)
  matterDocumentUploads: {
    find: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/matter_document_uploads",
        params: { matter: matterId },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_document_uploads/${id}`,
      }),
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/matter_document_uploads/search",
        data,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/matter_document_uploads",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/matter_document_uploads/${id}`,
        data,
      }),
    push: (categoryId: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_document_uploads/${categoryId}/${documentId}`,
      }),
    pull: (categoryId: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_document_uploads/${categoryId}/${documentId}`,
      }),
    queueValidation: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_document_uploads/${id}/queue_validation`,
      }),
    bulk: (data: { matter_document_upload: string; push?: string[]; pull?: string[] }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/matter_document_uploads/bulk",
        data,
      }),
  },

  // Signing Documents
  signingDocuments: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/signing/documents/${matterId}/matter`,
      }),
    search: (data: { filter: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/signing/documents/search",
        data,
      }),
    view: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/documents/${id}/view`,
      }),
    findWithEvents: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/documents/${id}/events`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/signing/documents",
        data,
      }),
    // Create signing document with file upload (for new PDFs)
    createWithFile: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: any }>("/v1/signing/documents", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing/documents/${id}`,
        data,
      }),
    void: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/signing/documents/${id}`,
        data: { ...data, status: "void" },
      }),
    // Download signed document
    download: (id: string) =>
      apiClient.get(`/v1/signing/documents/${id}/download`, {
        responseType: "blob",
      }),
    // Draft operations
    saveDraft: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/signing/documents/draft",
        data,
      }),
    saveDraftWithFile: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: any }>("/v1/signing/documents/draft", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    updateDraft: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing/documents/${id}/draft`,
        data,
      }),
    updateDraftWithFile: (id: string, formData: FormData) =>
      apiClient.patch<{ success: boolean; data: any }>(`/v1/signing/documents/${id}/draft`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    sendDraft: (id: string, data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: `/v1/signing/documents/${id}/send`,
        data,
      }),
    deleteDraft: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/signing/documents/${id}/draft`,
      }),
  },

  // Signing Templates
  signingTemplates: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number; searchString?: string }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/signing/templates/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/templates/${id}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/signing/templates",
        data,
      }),
    // Create signing template with file upload (for new templates)
    createWithFile: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: any }>("/v1/signing/templates", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/signing/templates/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/signing/templates/${id}`,
      }),
    getConfig: () =>
      request<{ data: { MAX_PARTIES: number; MAX_MARKUP_LENGTH: number; MAX_NAME_LENGTH: number; MAX_ANSWER_LENGTH: number } }>({
        method: "GET",
        url: "/v1/signing/templates/configuration",
      }),
    getFlatMatterData: (matterId: string) =>
      request<{ data: Record<string, any> }>({
        method: "GET",
        url: `/v1/signing/templates/flat_matter_data/${matterId}`,
      }),
    getFieldOptions: (divisionId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/signing/templates/field_options/${divisionId}`,
      }),
    download: (id: string) =>
      apiClient.get(`/v1/signing/templates/${id}/download`, {
        responseType: "arraybuffer",
      }),
  },

  // Contract Templates (document generation with signature blocks)
  contractTemplates: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number; searchString?: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/contract_templates/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/contract_templates/${id}`,
      }),
    create: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/contract_templates",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/contract_templates/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/contract_templates/${id}`,
      }),
    getFieldOptions: (divisionId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/contract_templates/field_options/${divisionId}`,
      }),
    preview: (id: string, data?: { matter_id?: string }) =>
      apiClient.post(`/v1/contract_templates/${id}/preview`, data || {}, {
        responseType: "arraybuffer",
      }),
  },

  // Contract Documents (generated from contract templates)
  contractDocuments: {
    generate: (data: { contract_template: string; matter: string; name?: string; parties: any[]; internal_answers?: Record<string, string>; send_emails_to?: string[]; email_sender?: string; move_to_recommended_step?: boolean }) =>
      request<{ data: { signing_document: any; contract_document: any } }>({
        method: "POST",
        url: "/v1/contract_documents/generate",
        data,
      }),
    generateDraft: (data: { contract_template: string; matter: string; name?: string; parties?: any[]; internal_answers?: Record<string, string>; send_emails_to?: string[]; move_to_recommended_step?: boolean }) =>
      request<{ data: { signing_document: any; contract_document: any } }>({
        method: "POST",
        url: "/v1/contract_documents/generate_draft",
        data,
      }),
    preview: (data: { contract_template: string; matter: string }) =>
      apiClient.post("/v1/contract_documents/preview", data, {
        responseType: "arraybuffer",
      }),
  },

  // Word Documents (templates)
  wordDocuments: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/word_documents/search",
        data,
      }),
    generate: (templateId: string, matterId: string) =>
      // Returns arraybuffer for file download
      apiClient.get(`/v1/word_documents/${templateId}/${matterId}/generate`, {
        responseType: "arraybuffer",
      }),
  },

  // Automation Logs
  automationLogs: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/automation_logs/matter/${matterId}`,
      }),
  },

  // Automation Assignments (Linear Automations)
  automationAssignments: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/automation_assignments/matter/${matterId}`,
      }),
    create: (data: { automation: string; matter: string; created_by?: string }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/automation_assignments",
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/automation_assignments/${id}`,
      }),
  },

  // State Automation Instances (Outreach Sequences)
  stateAutomationInstances: {
    findByMatter: (matterId: string) =>
      request<{ data: { active: any[]; paused: any[]; completed: any[]; resolved: any[] } }>({
        method: "GET",
        url: `/v1/state_automation_instances/matter/${matterId}`,
      }),
    getAvailable: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/state_automation_instances/available/${matterId}`,
      }),
    assign: (data: { template: string; matter: string }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/state_automation_instances",
        data,
      }),
    pause: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/pause`,
      }),
    resume: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/resume`,
      }),
    remove: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/state_automation_instances/${id}`,
      }),
    restart: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/state_automation_instances/${id}/restart`,
      }),
  },

  // Scheduled Texts
  scheduledTexts: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/scheduled_texts/matter/${matterId}`,
      }),
    create: (data: {
      contact: string
      to: string
      division: string
      matter: string
      body: string
      scheduled_for: number
      timezone: string
    }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/scheduled_texts",
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/scheduled_texts/${id}`,
      }),
  },

  // AI Follow-Up Threads
  aiFollowUpThreads: {
    search: (params: { filter?: { matter?: string }; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/ai_follow_up_threads/search",
        data: params,
      }),
    stop: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/ai_follow_up_threads/${id}/stop`,
      }),
  },

  // Portal Entries
  portalEntries: {
    findByMatter: (matterId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/portal_entries/${matterId}`,
      }),
    create: (data: { matter: string; communicator?: string }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/portal_entries",
        data,
      }),
    update: (id: string, data: { action_required?: boolean; action_required_message?: string }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/portal_entries/${id}`,
        data,
      }),
  },

  // Bankruptcy Module APIs
  modules: {
    bk: {
      // BK Cases - main case management
      cases: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/cases/${matterId}`,
          }),
        info: (matterId: string) =>
          request<{ success: boolean; data: { garnishments: number; objections: number } }>({
            method: "GET",
            url: `/v1/modules/bk/cases/${matterId}/info`,
          }),
        save: (matterId: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}`,
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/cases/${id}`,
            data,
          }),
        moveStage: (matterId: string, data: { stage: string }) =>
          request<{ success: boolean; data?: { case: any }; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}/stage`,
            data,
          }),
        addHearingResult: (id: string, data: { type: string; outcome: string; notes?: string; hearing_result_config?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/cases/${id}/hearing_result`,
            data,
          }),
        convert: (matterId: string, data: { chapter: number; date_chapter_conversion: string; roles?: Array<{ workflow_role: string; user: string }> }) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}/convert`,
            data,
          }),
        sendForStudentLoanReview: (matterId: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/cases/${matterId}/student_loan_review`,
          }),
      },
      // Hearing Result Configs - configurable outcomes per workflow
      hearingResultConfigs: {
        findByWorkflow: (workflowId: string, hearingType?: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/hearing_result_configs/workflow/${workflowId}`,
            params: hearingType ? { hearing_type: hearingType } : undefined,
          }),
      },
      // Docket Entries - court docket records from Pacer
      docketEntries: {
        find: (courtCode: string, caseNumber: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/docket_entries/${courtCode}/${caseNumber}`,
          }),
      },
      // Claims - creditor claims from Pacer
      claims: {
        find: (courtCode: string, caseNumber: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/claims/${courtCode}/${caseNumber}`,
          }),
      },
      // Court Drive - Pacer sync integration
      courtDrive: {
        sync: (data: { division: string; court_code: string; case_number: string; resource: "dockets" | "claims" }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/court_drive/sync",
            data,
            timeout: 120000,
          }),
        sendDischargeCreditReports: (data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/court_drive/send_discharge_credit_reports",
            data,
          }),
        reExtractDates: (data: { division: string; court_code: string; case_number: string }) =>
          request<{ success: boolean; data?: any }>({
            method: "POST",
            url: "/v1/modules/bk/court_drive/re_extract_dates",
            data,
          }),
      },
      // Workflow Configs - per-division+workflow BK config (schedule doc mappings, etc.)
      workflowConfigs: {
        findByDivisionAndWorkflow: (division: string, workflow: string) =>
          request<{ data: any }>({
            method: "GET",
            url: `/v1/modules/bk/workflow_configs/${division}/${workflow}`,
          }),
      },
      // Districts - court district configuration
      districts: {
        search: (data: any) =>
          request<{ data: { documents: any[] } }>({
            method: "POST",
            url: "/v1/modules/bk/districts/search",
            data,
          }),
      },
      // Questionnaires
      questionnaires: {
        findByMatter: (matterId: string) =>
          request<{ data: any }>({
            method: "GET",
            url: `/v1/modules/bk/questionnaires/${matterId}`,
          }),
        config: () =>
          request<{ data: any }>({
            method: "GET",
            url: "/v1/modules/bk/questionnaires/config",
          }),
        create: (data: { matter: string; contact: string }) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/questionnaires",
            data,
          }),
        save: (id: string, data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/questionnaires/${id}`,
            data,
          }),
        qualify: (id: string, data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/questionnaires/${id}/qualify`,
            data,
          }),
        calculations: {
          importLexus: (data: { questionnaire_id: string; document_id: string; import_type: "contact" | "spouse" }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: "/v1/modules/bk/questionnaires/calculations/import_lexus",
              data,
            }),
          monthlyExpenses: (data: { division: string; state: string; dependents: number; spouse: boolean; livingType: string }) =>
            request<{ success: boolean; data?: Array<{ prop: string; text: string; value: number }> }>({
              method: "POST",
              url: "/v1/modules/bk/questionnaires/calculations/monthly_expenses",
              data,
            }),
          vehicleDetails: (data: { matter: string; vin: string }) =>
            request<{ success: boolean; data?: any }>({
              method: "POST",
              url: "/v1/modules/bk/questionnaires/calculations/vehicle_details",
              data,
            }),
          creditorInfo: (data: { matter: string; creditor_name: string }) =>
            request<{ success: boolean; data?: any }>({
              method: "POST",
              url: "/v1/modules/bk/questionnaires/calculations/creditor_info",
              data,
            }),
        },
      },
      // Credit Counseling Courses (CCC)
      ccc: {
        findByMatter: (matterId: string) =>
          request<{ data: any }>({
            method: "GET",
            url: `/v1/modules/bk/credit_counseling_courses/${matterId}`,
          }),
        update: (
          id: string,
          data: {
            first_course_status?: string
            first_course_document?: string
            second_course_status?: string
            second_course_document?: string
          }
        ) =>
          request<{ success: boolean }>({
            method: "PATCH",
            url: `/v1/modules/bk/credit_counseling_courses/${id}`,
            data,
          }),
        upload: (data: { document: string; type?: "first" | "second" }) =>
          request<{ success: boolean; data?: any }>({
            method: "POST",
            url: "/v1/modules/bk/credit_counseling_courses",
            data,
          }),
      },
      // Credit Reports
      creditReports: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/credit_reports/${matterId}`,
          }),
        pull: (data: { matter: string; contact: string; report_type: string; photo_id_document?: string }) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/credit_reports/download",
            data,
            timeout: 120000,
          }),
      },
      // Creditors - full CRUD
      creditors: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_creditors/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_creditors/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_creditors",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_creditors/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_creditors/${id}`,
          }),
      },
      // Garnishments - full CRUD with sub-resources
      garnishments: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_garnishments/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_garnishments",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_garnishments/${id}`,
            data: { deleted: true },
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/note`,
              data,
            }),
        },
        checks: {
          add: (id: string, data: { date: string; memo?: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
          update: (id: string, data: { check_id: string; date?: string; memo?: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "PATCH",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
          delete: (id: string, data: { check_id: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_garnishments/${id}/check`,
              data,
            }),
        },
        documents: {
          add: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_garnishments/${id}/document`,
              data,
            }),
          delete: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_garnishments/${id}/document`,
              data,
            }),
        },
      },
      // Objections - full CRUD with sub-resources
      objections: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/bk_objections/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_objections/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_objections",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_objections/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_objections/${id}`,
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_objections/${id}/note`,
              data,
            }),
        },
        documents: {
          add: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_objections/${id}/document`,
              data,
            }),
          delete: (id: string, data: { document: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "DELETE",
              url: `/v1/modules/bk/bk_objections/${id}/document`,
              data,
            }),
        },
      },
      // Trustee Uploads - full CRUD with workflow actions
      trusteeUploads: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: { document_needed: any[]; ready_for_upload: any[]; uploaded: any[]; all: any[] } }>({
            method: "GET",
            url: `/v1/modules/bk/bk_trustee_uploads/${matterId}/matter`,
          }),
        findById: (id: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
            data,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}`,
          }),
        search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
          request<{ data: any[] }>({
            method: "POST",
            url: "/v1/modules/bk/bk_trustee_uploads/search",
            data,
          }),
        changeStage: (id: string, data: { to_stage: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/stage`,
            data,
          }),
        markUploaded: (id: string, data: { upload_confirmation_notes?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/mark_uploaded`,
            data,
          }),
        markUnableToComplete: (id: string, data: { notes?: string }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "POST",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/mark_unable_to_complete`,
            data,
          }),
        reassign: (id: string, data: { assigned_to: string[] }) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/bk_trustee_uploads/${id}/reassign`,
            data,
          }),
        notes: {
          add: (id: string, data: { body: string }) =>
            request<{ success: boolean; message?: string[] }>({
              method: "POST",
              url: `/v1/modules/bk/bk_trustee_uploads/${id}/note`,
              data,
            }),
        },
      },
      // Module configuration
      config: () =>
        request<{ success: boolean; data: any }>({
          method: "GET",
          url: "/v1/modules/bk/config",
        }),
      // New Case Entries - recently filed cases from Pacer
      newCaseEntries: {
        find: (divisionId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/new_case_entries/${divisionId}`,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/new_case_entries/${id}`,
          }),
      },
      // Dismissed Entries - cases with dismissal docket entries
      dismissedEntries: {
        find: (divisionId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/dismissed_entries/${divisionId}`,
          }),
        acknowledge: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/dismissed_entries/${id}/acknowledge`,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/dismissed_entries/${id}`,
          }),
      },
      // Converted Entries - cases with chapter conversion docket entries
      convertedEntries: {
        find: (divisionId: string) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/converted_entries/${divisionId}`,
          }),
        acknowledge: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/bk/converted_entries/${id}/acknowledge`,
          }),
        delete: (id: string) =>
          request<{ success: boolean; message?: string[] }>({
            method: "DELETE",
            url: `/v1/modules/bk/converted_entries/${id}`,
          }),
      },
      // Item Type Configs - workflow-driven item types for stages
      itemTypeConfigs: {
        findByWorkflow: (workflowId: string, context: string, chapter?: number) =>
          request<{ success: boolean; data: any[] }>({
            method: "GET",
            url: `/v1/modules/bk/item_type_configs/workflow/${workflowId}`,
            params: { context, chapter },
          }),
      },
      // Social Security Numbers - for credit report validation
      socialSecurityNumbers: {
        findByContact: (contactId: string) =>
          request<{ success: boolean; data: string | null }>({
            method: "GET",
            url: `/v1/social_security_numbers/${contactId}`,
          }),
      },
      // Supervisor views
      supervisor: {
        hearings: (divisionId: string, data: { start: number; end: number }) =>
          request<{ success: boolean; data: any[] }>({
            method: "POST",
            url: `/v1/modules/bk/supervisor/${divisionId}/hearings`,
            data,
          }),
      },
      // Filing - Case Preparation (pre-filing form management)
      filing: {
        // Filings CRUD
        filings: {
          find: (matterId: string) =>
            request<{ success: boolean; data: any[] }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/${matterId}/matter`,
            }),
          findById: (id: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/${id}`,
            }),
          create: (data: { matter: string; chapter: number; case_type: string; court?: any }) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings",
              data,
            }),
          update: (id: string, data: any) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/${id}`,
              data,
            }),
          delete: (id: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/filings/${id}`,
            }),
          convertChapter: (
            matterId: string,
            data: { target_chapter: number; date_chapter_conversion?: string }
          ) =>
            request<{
              success: boolean
              data?: { filing_converted: boolean; case_converted: boolean; target_chapter: number }
              message?: string[]
            }>({
              method: "POST",
              url: `/v1/modules/bk/filing/filings/${matterId}/convert-chapter`,
              data,
            }),
        },
        // Forms - config and form data
        forms: {
          getConfig: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/forms/config",
            }),
          getConfigByChapter: (chapter: number) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/config/chapter/${chapter}`,
            }),
          getConfigByType: (formType: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/config/${formType}`,
            }),
          getData: (filingId: string, formType: string) =>
            request<{ success: boolean; data: any; calculated_fields?: any; status?: string; common_fields?: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/${filingId}/${formType}`,
            }),
          save: (filingId: string, formType: string, formData: Record<string, unknown>) =>
            request<{ success: boolean }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/forms/${filingId}/${formType}`,
              data: { data: formData },
            }),
          getSaveUrl: (filingId: string, formType: string) =>
            `${keys.API_URL}/v1/modules/bk/filing/forms/${filingId}/${formType}`,
          calculateSummary: (filingId: string) =>
            request<{ success: boolean; calculated_fields: any; warnings: string[]; missing_forms: string[]; applied: boolean }>({
              method: "POST",
              url: `/v1/modules/bk/filing/forms/${filingId}/calculate-summary`,
              data: { apply: true },
            }),
          getCreditorOptions: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/${filingId}/creditor-options`,
            }),
        },
        // Schedule C (exemptions)
        scheduleC: {
          get: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c`,
            }),
          update: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c`,
              data: { data },
            }),
          getExemptionConfig: (filingId: string, state?: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/exemptions/config`,
              params: state ? { state } : undefined,
            }),
          addExemption: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/exemptions`,
              data,
            }),
          updateExemption: (filingId: string, exemptionId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/exemptions/${exemptionId}`,
              data,
            }),
          deleteExemption: (filingId: string, exemptionId: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/exemptions/${exemptionId}`,
            }),
          validateExemption: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/exemptions/validate`,
              data,
            }),
          validateAll: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/validate-all`,
            }),
          hideProperty: (filingId: string, propertyId: string, reason: string) =>
            request<{ success: boolean }>({
              method: "POST",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/hide-property`,
              data: { propertyId, reason },
            }),
          unhideProperty: (filingId: string, propertyId: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/hide-property/${propertyId}`,
            }),
          getHiddenProperties: (filingId: string) =>
            request<{ success: boolean; data: any[] }>({
              method: "GET",
              url: `/v1/modules/bk/filing/forms/${filingId}/b106c/hidden-properties`,
            }),
        },
        // Means Test
        meansTest: {
          analyze: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/means-test/${filingId}/analyze`,
            }),
          getMedianIncome: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/means-test/${filingId}/median-income`,
            }),
          calculate: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/calculate`,
            }),
          calculateChapter7Full: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/calculate-chapter7-full`,
            }),
          calculateChapter13Plan: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/calculate-chapter13-plan`,
            }),
          saveIncome: (filingId: string, incomeData: Record<string, unknown>) =>
            request<{ success: boolean }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/save-income`,
              data: incomeData,
            }),
          complete: (filingId: string) =>
            request<{ success: boolean }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/complete`,
            }),
          getDraft: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/means-test/${filingId}/draft`,
            }),
          getStandards: (params: { state: string; county?: string; household_size?: number }) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/means-test/standards",
              params,
            }),
          getDataStatus: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/means-test/data-status",
            }),
          calculateDeductions: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/calculate-deductions`,
            }),
          compareChapters: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/compare`,
            }),
          checkStaleness: (filingId: string) =>
            request<{ success: boolean; data: MeansTestStaleness }>({
              method: "GET",
              url: `/v1/modules/bk/filing/means-test/${filingId}/staleness`,
            }),
          previewRefresh: (filingId: string) =>
            request<{ success: boolean; data: MeansTestPreviewRefresh }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/preview-refresh`,
            }),
          applyRefresh: (filingId: string, body: { accepted: string[]; rejected: string[] }) =>
            request<{ success: boolean; data: { applied: boolean; accepted: number; rejected: number } }>({
              method: "POST",
              url: `/v1/modules/bk/filing/means-test/${filingId}/apply-refresh`,
              data: body,
            }),
        },
        // Income History (6-month CMI)
        incomeHistory: {
          getAll: (filingId: string) =>
            request<{ success: boolean; data: { filing: string; months: any[] } }>({
              method: "GET",
              url: `/v1/modules/bk/filing/income-history/${filingId}`,
            }),
          getPeriods: (filingId: string) =>
            request<{ success: boolean; data: { filing: string; periods: any[] } }>({
              method: "GET",
              url: `/v1/modules/bk/filing/income-history/${filingId}/periods`,
            }),
          getSummary: (filingId: string) =>
            request<{ success: boolean; data: { filing: string; debtor1: any; debtor2: any } }>({
              method: "GET",
              url: `/v1/modules/bk/filing/income-history/${filingId}/summary`,
            }),
          upsert: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/income-history/${filingId}`,
              data,
            }),
          update: (filingId: string, monthIndex: number, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PUT",
              url: `/v1/modules/bk/filing/income-history/${filingId}/${monthIndex}`,
              data,
            }),
          delete: (filingId: string, monthIndex: number, debtor?: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/income-history/${filingId}/${monthIndex}`,
              params: debtor ? { debtor } : undefined,
            }),
        },
        // Plan calculations (Ch13)
        plan: {
          getData: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/plan/${filingId}/plan-data`,
            }),
          calculate: (filingId: string, planData: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/calculate",
              data: { filingId, planData },
            }),
          liquidationTest: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/liquidation-test",
              data: { filingId },
            }),
          confirmationChecklist: (filingId: string, planData: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/confirmation-checklist",
              data: { filingId, planData },
            }),
          trusteeFeeRate: (state: string, district?: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: district
                ? `/v1/modules/bk/filing/plan/trustee-fee-rate/${state}/${district}`
                : `/v1/modules/bk/filing/plan/trustee-fee-rate/${state}`,
            }),
          tillRate: (primeRate?: number, riskAdjustment?: number) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/till-rate",
              data: { primeRate, riskAdjustment },
            }),
          check910Vehicle: (purchaseDate: string, filingDate: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/910-vehicle",
              data: { purchaseDate, filingDate },
            }),
          cramdown: (vehicleValue: number, debtBalance: number, purchaseDate?: string, filingDate?: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/cramdown",
              data: { vehicleValue, debtBalance, purchaseDate, filingDate },
            }),
          lienStrip: (propertyValue: number, firstLienBalance: number, secondLienBalance: number) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/plan/lien-strip",
              data: { propertyValue, firstLienBalance, secondLienBalance },
            }),
          saveSecuredTreatment: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/plan/${filingId}/secured-treatment`,
              data,
            }),
          paymentSchedule: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/plan/${filingId}/payment-schedule`,
              data,
            }),
          scenarios: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/plan/${filingId}/scenarios`,
              data,
            }),
          // Plan Drafts
          drafts: {
            list: (filingId: string) =>
              request<{ success: boolean; data: any[] }>({
                method: "GET",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts`,
              }),
            get: (filingId: string, draftId: string) =>
              request<{ success: boolean; data: any }>({
                method: "GET",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts/${draftId}`,
              }),
            create: (filingId: string, data: Record<string, unknown>) =>
              request<{ success: boolean; data: any }>({
                method: "POST",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts`,
                data,
              }),
            update: (filingId: string, draftId: string, data: Record<string, unknown>) =>
              request<{ success: boolean; data: any }>({
                method: "PATCH",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts/${draftId}`,
                data,
              }),
            delete: (filingId: string, draftId: string) =>
              request<{ success: boolean }>({
                method: "DELETE",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts/${draftId}`,
              }),
            compare: (filingId: string, draftIds: string[]) =>
              request<{ success: boolean; data: any[] }>({
                method: "POST",
                url: `/v1/modules/bk/filing/plan/${filingId}/drafts/compare`,
                data: { draftIds },
              }),
          },
        },
        // PDF generation and preview
        pdf: {
          generate: (filingId: string, formType: string) =>
            request<any>({
              method: "POST",
              url: `/v1/modules/bk/filing/pdf/generate/${filingId}/${formType}`,
            }),
          generateBlob: async (filingId: string, formType: string): Promise<Blob> => {
            const response = await request<{
              success: boolean
              data?: { binary: { type: string; data: number[] }; page_count: number; errors: string[]; warnings: string[] }
              message?: string[]
            }>({
              method: "POST",
              url: `/v1/modules/bk/filing/pdf/generate/${filingId}/${formType}`,
              data: { options: { flatten: true, generateContinuations: true } },
            })
            const bufferData = response.data?.binary?.data
            if (!bufferData) {
              throw new Error("No PDF data returned from server")
            }
            return new Blob([new Uint8Array(bufferData)], { type: "application/pdf" })
          },
          generatePackageBlob: async (filingId: string): Promise<Blob> => {
            const response = await request<{
              success: boolean
              data?: { binary: { type: string; data: number[] }; page_count: number; errors: string[]; warnings: string[] }
              message?: string[]
            }>({
              method: "POST",
              url: `/v1/modules/bk/filing/pdf/package/${filingId}`,
              data: { format: "binary", include_incomplete: false },
            })
            const bufferData = response.data?.binary?.data
            if (!bufferData) {
              throw new Error("No PDF data returned from server")
            }
            return new Blob([new Uint8Array(bufferData)], { type: "application/pdf" })
          },
          getPreviewUrl: (filingId: string, formType: string) =>
            `${keys.API_URL}/v1/modules/bk/filing/pdf/preview/${filingId}/${formType}`,
        },
        // Validation
        validation: {
          getResults: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/validation/filing/${filingId}/complete`,
            }),
          crossSchedule: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/validation/cross-schedule",
              data: { filingId },
            }),
          getCrossSchedule: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/validation/filing/${filingId}/cross-schedule`,
            }),
        },
        // Properties
        properties: {
          findByFiling: (filingId: string) =>
            request<{ success: boolean; data: any[] }>({
              method: "GET",
              url: `/v1/modules/bk/filing/properties/filing/${filingId}`,
            }),
          create: (data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/properties",
              data,
            }),
          update: (id: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/properties/${id}`,
              data,
            }),
          delete: (id: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/properties/${id}`,
            }),
        },
        // Common Info
        commonInfo: {
          get: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/${filingId}/common-info`,
            }),
          update: (filingId: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/${filingId}/common-info`,
              data,
            }),
        },
        // Reaffirmation Agreements (Ch7)
        reaffirmations: {
          findByFiling: (filingId: string) =>
            request<{ success: boolean; data: any[] }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/${filingId}/reaffirmations`,
            }),
          create: (data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/reaffirmations",
              data,
            }),
          update: (id: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}`,
              data,
            }),
          delete: (id: string) =>
            request<{ success: boolean }>({
              method: "DELETE",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}`,
            }),
          sign: (id: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}/sign`,
              data,
            }),
          file: (id: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}/file`,
              data,
            }),
          rescind: (id: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}/rescind`,
            }),
          analyze: (id: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/reaffirmations/${id}/analyze`,
            }),
        },
        // Statement of Intention (Ch7)
        statementOfIntention: {
          get: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/filings/${filingId}/statement-of-intention`,
            }),
          generate: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/filings/${filingId}/statement-of-intention/generate`,
            }),
          update: (id: string, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/statement-of-intention/${id}`,
              data,
            }),
          updateSecured: (id: string, index: number, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/statement-of-intention/${id}/secured/${index}`,
              data,
            }),
          updateLease: (id: string, index: number, data: Record<string, unknown>) =>
            request<{ success: boolean; data: any }>({
              method: "PATCH",
              url: `/v1/modules/bk/filing/filings/statement-of-intention/${id}/lease/${index}`,
              data,
            }),
        },
        // Filing Package
        filingPackage: {
          creditorMatrix: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/creditor-matrix",
              data: { filingId },
            }),
          top20Unsecured: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/creditor-matrix/top20",
              data: { filingId },
            }),
          exportCreditorMatrix: (filingId: string, format: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/creditor-matrix/export",
              data: { filingId, format },
            }),
          detectDuplicates: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/duplicates/detect",
              data: { filingId },
            }),
          checklistSummary: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/checklist/summary",
              data: { filingId },
            }),
          checklistRequirements: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/filings/filing-package/checklist/requirements",
            }),
          feeWaiverEligibility: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/fees/waiver-eligibility",
              data: { filingId },
            }),
          feeRecommendation: (filingId: string, chapter: number) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/fees/recommend",
              data: { filingId, chapter },
            }),
          installmentSchedule: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/fees/installment-schedule",
              data: { filingId },
            }),
          feeAmounts: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/filings/filing-package/fees/amounts",
            }),
          generate: (filingId: string) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: "/v1/modules/bk/filing/filings/filing-package/generate",
              data: { filingId },
            }),
        },
        // Reference Data (constants, states, districts)
        referenceData: {
          getDistricts: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/constants/districts",
            }),
          getDistrictsByState: (stateCode: string) =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: `/v1/modules/bk/filing/constants/districts/${stateCode}`,
            }),
          getStates: () =>
            request<{ success: boolean; data: any }>({
              method: "GET",
              url: "/v1/modules/bk/filing/constants/states",
            }),
        },
        // Sync services
        sync: {
          creditors: (data: { matter: string; filing: string }) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/sync/${data.filing}/creditors`,
              data,
            }),
          matterInfo: (data: { matter: string; filing: string }) =>
            request<{ success: boolean; data: any }>({
              method: "POST",
              url: `/v1/modules/bk/filing/sync/${data.filing}/matter-info`,
              data,
            }),
        },
      },
    },
    // PSF (Personal Settlement Funding) Module
    psf: {
      intake: {
        findByMatter: (matterId: string) =>
          request<{ success: boolean; data: any }>({
            method: "GET",
            url: `/v1/modules/psf/intake/${matterId}`,
          }),
        create: (data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "POST",
            url: "/v1/modules/psf/intake",
            data,
          }),
        update: (id: string, data: any) =>
          request<{ success: boolean; data?: any; message?: string[] }>({
            method: "PATCH",
            url: `/v1/modules/psf/intake/${id}`,
            data,
          }),
      },
    },
  },

  // Claimables
  claimables: {
    find: (division: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/claimables/${division}`,
      }),
    findAll: (division: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/claimables/${division}/all`,
      }),
    claim: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/claimables/${id}/claim`,
      }),
    findByMatter: (matterId: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/claimables/${matterId}/matter`,
      }),
    create: (data: { matter: string; name: string; allow_duplicates?: boolean }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/claimables/create",
        data,
      }),
  },

  // Claimable Templates
  claimableTemplates: {
    find: (workflowId?: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/claimable_templates",
        params: workflowId ? { workflow: workflowId } : undefined,
      }),
  },

  // Call Center
  callCenter: {
    accessToken: () =>
      request<{ data: { token: string } }>({
        method: "GET",
        url: "/v1/call_center/access_token",
      }),
    matterInfo: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_center/${id}/matter`,
      }),
    contactInfo: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_center/${id}/contact`,
      }),
    addCall: (data: {
      user?: string | null
      contact?: string | null
      type: string
      userNumber?: string
      contactNumber?: string
      number: string
      callSid: string
    }) =>
      request<{ success: boolean; data?: any }>({
        method: "POST",
        url: "/v1/call_center/add_call",
        data,
      }),
    mergeCall: (data: { callSid: string; transferId?: string; type?: string; user?: string; contact?: string; number?: string }) =>
      request<{ success: boolean; data?: { status?: string } }>({
        method: "POST",
        url: "/v1/call_center/merge_call",
        data,
      }),
    cancelTransfer: (data: { callSid: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/call_center/cancel_transfer",
        data,
      }),
    hangup: (data: { callSid: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/call_center/hangup",
        data,
      }),
    hold: (data: { callSid: string }) =>
      request<{ success: boolean; data?: { status?: string } }>({
        method: "POST",
        url: "/v1/call_center/hold",
        data,
      }),
    checkTransferStatus: (callSid: string) =>
      request<{ success: boolean; data?: { answered: boolean } }>({
        method: "GET",
        url: `/v1/call_center/check_transfer_status?callSid=${callSid}`,
      }),
    agentCallState: () =>
      request<{
        success: boolean
        data: {
          data: {
            call_id: string
            call_sid: string
            name: string
            number: string
            contact: string | null
            matter: string | null
            direction: string | null
          } | null
        }
      }>({
        method: "GET",
        url: "/v1/call_center/agent_call_state",
      }),
  },

  // Call Phone Numbers
  call_phone_numbers: {
    search: (params: {
      searchString?: string
      filter?: Record<string, any>
      limit?: number
      skip?: number
      sort?: Record<string, number>
    }) =>
      request<{ success: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_phone_numbers/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_phone_numbers/${id}`,
      }),
  },

  // Call Queue Entries
  callQueueEntries: {
    check: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/call_queue_entries/check",
      }),
  },

  // Call Offers
  callOffers: {
    decline: (callId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/call_offers/${callId}/decline`,
      }),
  },

  // Calls (Phone Call Logs)
  calls: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/calls/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/calls/${id}`,
      }),
  },

  // Call Queues (Queue Metrics)
  callQueues: {
    agentMetrics: (data: { start: number; end: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/call_queues/agent_metrics",
        data,
      }),
  },

  // Divisions
  divisions: {
    find: () =>
      request<{ data: Division[] }>({
        method: "GET",
        url: "/v1/divisions",
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: Division[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/divisions/search",
        data: params,
      }),
  },

  // Division Departments
  division_departments: {
    search: (params: SearchParams) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/division_departments/search",
        data: params,
      }),
    findUsers: (id: string) =>
      request<{ data: string[] }>({
        method: "GET",
        url: `/v1/division_departments/${id}/users`,
      }),
  },

  // Workflows
  workflows: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/workflows",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflows/${id}`,
      }),
    search: (data: { filter?: Record<string, unknown>; searchString?: string; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/workflows/search",
        data,
      }),
  },

  // Pipelines
  pipelines: {
    findByUser: (userId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/pipelines/${userId}/user`,
      }),
    find: (workflow: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/pipelines",
        params: { workflow },
      }),
  },

  // Workflow Steps
  workflowSteps: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/workflow_steps/${id}`,
      }),
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/workflow_steps/search",
        data,
      }),
  },

  // Matter Closing Reasons
  matterClosingReasons: {
    findByWorkflow: (workflowId: string) =>
      request<{ success: boolean; data: Array<{ _id: string; name: string }> }>({
        method: "GET",
        url: `/v1/matter_closing_reasons/${workflowId}/workflow`,
      }),
  },

  // Dashboard
  dashboard: {
    sidebar: (divisionId: string) =>
      request<{ data: SidebarCounts }>({
        method: "GET",
        url: `/v1/dashboard/sidebar/${divisionId}`,
      }),
    overview: (divisionId: string) =>
      request<{
        startOfWeek: number
        endOfWeek: number
        data: {
          tasks_this_week: number
          tasks_completed_this_week: number
          events_this_week: number
          events_completed_this_week: number
          tasks_overdue: number
          events_overdue: number
          documents_pending: number
          matters_overdue: number
          emails: number
          text_threads: number
        }
      }>({
        method: "GET",
        url: `/v1/dashboard/overview/${divisionId}`,
      }),
  },

  // Notifications
  notifications: {
    count: () =>
      request<{ data: { count: number } }>({
        method: "GET",
        url: "/v1/notifications",
      }),
    search: (params?: { filter?: Record<string, any>; limit?: number; skip?: number; searchString?: string }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/notifications/search",
        data: params,
      }),
    update: (id: string, data: { read_at: number }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/notifications/${id}`,
        data,
      }),
    markAllRead: () =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/notifications/mark_all_read",
      }),
    archive: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/notifications/${id}/archive`,
      }),
    bulkUpdate: (data: { ids: string[]; action: "mark_read" | "archive" }) =>
      request<{ success: boolean; data: { modified: number } }>({
        method: "POST",
        url: "/v1/notifications/bulk_update",
        data,
      }),
  },

  // Notification Preferences
  notificationPreferences: {
    find: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/notification_preferences",
      }),
    update: (data: Record<string, any>) =>
      request<{ data: any }>({
        method: "PUT",
        url: "/v1/notification_preferences",
        data,
      }),
    categories: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/notification_preferences/categories",
      }),
  },

  // Push Subscriptions (Web Push API)
  pushSubscriptions: {
    subscribe: (data: { endpoint: string; p256dh: string; auth: string; user_agent: string }) =>
      request<{ success: boolean }>({ method: "POST", url: "/v1/push_subscriptions/subscribe", data }),
    unsubscribe: (data: { endpoint: string }) =>
      request<{ success: boolean }>({ method: "POST", url: "/v1/push_subscriptions/unsubscribe", data }),
    vapidPublicKey: () =>
      request<{ data: string }>({ method: "GET", url: "/v1/push_subscriptions/vapid_public_key" }),
  },

  // Notification Subscriptions (per-matter)
  notificationSubscriptions: {
    findByMatter: (matterId: string) =>
      request<{ data: Array<{ _id: string; type: string; matter: string }> }>({
        method: "GET",
        url: `/v1/notification_subscriptions/${matterId}`,
      }),
    create: (data: { matter: string; type: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/notification_subscriptions",
        data,
      }),
    delete: (data: { matter: string; type: string }) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: "/v1/notification_subscriptions",
        data,
      }),
  },

  // Load (app configuration)
  load: {
    standard: () =>
      request<{ data: { DOMAINS: Record<string, string>; PLATFORM?: any } }>({
        method: "GET",
        url: "/v1/load/standard",
      }),
  },

  // Query (batch operations)
  query: {
    batch: (collections: Record<string, string[]>) =>
      request<{ data: Record<string, any[]> }>({
        method: "POST",
        url: "/v1/query/batch",
        data: collections,
      }),
  },

  // Support Messages (Live Chat)
  supportMessages: {
    create: (data: { matter: string; body: string }) =>
      request<{ data: { support_message: any; support_thread: any } }>({
        method: "POST",
        url: "/v1/support_messages",
        data,
      }),
  },

  // AI Communication Threads (used for live chat conversation history)
  aiCommunicationThreads: {
    findByMatter: (matterId: string, channel: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/ai_communication_threads/${matterId}/${channel}`,
      }),
    getResponse: (data: { matter: string; channel: string }) =>
      request<{ data: string }>({
        method: "POST",
        url: "/v1/ai_communication_threads/get_response",
        data,
      }),
  },

  // Texts
  texts: {
    send: (data: {
      contact: string
      division: string
      to: string
      body?: string
      matter?: string
      task?: string
      attachments?: Array<{ data: string; type: string; name: string }>
    }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/texts",
        data,
      }),
  },

  // Text Threads
  textThreads: {
    search: (params: {
      filter?: { assigned?: boolean; unreturned?: boolean; division?: string }
      searchString?: string
      limit?: number
      skip?: number
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/text_threads/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/text_threads/${id}`,
      }),
    findByContactDivision: (contactId: string, divisionId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/text_threads/${contactId}/${divisionId}`,
      }),
    read: (id: string, data: { read: boolean }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/text_threads/${id}`,
        data,
      }),
    setAssignedUsers: (id: string, data: { assigned_users: string[] }) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/text_threads/${id}/assigned_users`,
        data,
      }),
  },

  // Call Voicemails
  callVoicemails: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/call_voicemails/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/call_voicemails/${id}`,
      }),
    resolve: (id: string) =>
      request<{ success: boolean }>({
        method: "PATCH",
        url: `/v1/call_voicemails/${id}/resolve`,
      }),
  },

  // Text Templates
  textTemplates: {
    search: (data: { searchString?: string; limit?: number }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/text_templates/search",
        data,
      }),
  },

  // Note Templates
  noteTemplates: {
    search: (data: { searchString?: string; filter?: Record<string, unknown>; limit?: number; skip?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/note_templates/search",
        data,
      }),
    userSearch: (data: { searchString?: string; filter?: Record<string, unknown>; limit?: number; skip?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/note_templates/user/search",
        data,
      }),
    userCreate: (data: { division: string; workflow?: string; name: string; body: string }) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/note_templates/user",
        data,
      }),
    userUpdate: (id: string, data: { name: string; body: string }) =>
      request<{ data: any }>({
        method: "PATCH",
        url: `/v1/note_templates/user/${id}`,
        data,
      }),
    userDelete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/note_templates/user/${id}`,
      }),
  },

  // Inject Values (for template variable substitution)
  injectValues: {
    string: (data: { string: string; contact?: string; matter?: string; hideErrors?: boolean }) =>
      request<{ data: string }>({
        method: "POST",
        url: "/v1/inject_values/string",
        data,
      }),
  },

  // Leads
  leads: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
      isCSV?: boolean
    }) =>
      request<{ success: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/leads/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "GET",
        url: `/v1/leads/${id}`,
      }),
    claim: (id: string) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: `/v1/leads/${id}/claim`,
      }),
  },

  // Nylas (Email/Calendar Sync)
  nylas: {
    login: (division: string) =>
      request<{ data: { url: string } }>({
        method: "GET",
        url: `/v1/nylas/login`,
        params: { division },
      }),
  },

  // Nylas Email Grants
  nylasEmailGrants: {
    find: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/nylas_email_grants",
      }),
  },

  // Booking Links
  bookingLinks: {
    findMine: () =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/booking_links/me",
      }),
    createMine: (data: { type: number; division: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/booking_links/me",
        data,
      }),
    search: (data: { division: string; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/booking_links/search",
        data,
      }),
  },

  // Company Documents (avatars, logos, etc.)
  companyDocuments: {
    search: (data: { filter?: Record<string, unknown>; skip?: number; limit?: number; sort?: Record<string, number> }) =>
      request<{ data: any[] }>({
        method: "POST",
        url: "/v1/company_documents/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/company_documents/${id}`,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/company_documents/${id}`,
      }),
    // Upload goes to processing service (port 5002)
    upload: (formData: FormData) =>
      axios.post<{ success: boolean; data: any; message?: string[] }>(`${keys.PROCESSING_URL}/v1/company_documents`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${keys.SYSTEM_API_KEY}`,
        },
      }),
  },

  // Emails
  emails: {
    compose: (data: { division: string; matter?: string; contact?: string; email_thread?: string; email_template?: string; task?: string }) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "POST",
        url: "/v1/emails/compose",
        data,
      }),
    send: (data: {
      from: string
      to: Array<{ name: string; email: string; _id?: string }>
      attachments: Array<{ content: string; content_type: string; filename: string; is_inline: boolean; size: number }>
      subject: string
      body: string
      task?: string
      matter?: string
      email_template?: string
      email_sender?: string
      reply_to_message_id?: string
    }) =>
      request<{ success: boolean; data?: { email: string; thread?: string; support_ticket?: string }; message?: string[] }>({
        method: "POST",
        url: "/v1/emails",
        data,
      }),
    getAttachments: (id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/emails/${id}/attachments`,
      }),
  },

  // Email Templates
  emailTemplates: {
    search: (params: { searchString?: string; filter?: { workflow?: string; division?: string }; limit?: number; skip?: number }) =>
      request<{ success?: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/email_templates/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/email_templates/${id}`,
      }),
  },

  // Email Senders (SendGrid configurations)
  emailSenders: {
    search: (params: { searchString?: string; filter?: { division?: string }; limit?: number; skip?: number }) =>
      request<{ success?: boolean; data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/email_senders/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/email_senders/${id}`,
      }),
  },

  // AI Email
  aiEmail: {
    call: (callId: string) =>
      request<{ success: boolean; data?: { email: { subject: string; body: string } }; message?: string[] }>({
        method: "GET",
        url: `/v1/ai/email/call/${callId}`,
      }),
    item: (itemId: string) =>
      request<{ success: boolean; data?: { email: { subject: string; body: string } }; message?: string[] }>({
        method: "GET",
        url: `/v1/ai/email/item/${itemId}`,
      }),
    regenerate: (data: { body: string; prompt: string; matter?: string; contact?: string }) =>
      request<{ success: boolean; data?: { email: { subject: string; body: string } }; message?: string[] }>({
        method: "POST",
        url: "/v1/ai/email/regenerate",
        data,
      }),
  },

  // Events
  events: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/events/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/events/${matterId}/matter`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/events",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/events/${id}`,
        data,
      }),
    finish: (id: string, data?: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/events/${id}/finish`,
        data,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/events/search",
        data: params,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/events/${id}`,
      }),
  },

  // Calendar Events
  calendarEvents: {
    calendar: (start: number, end: number, user: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/calendar_events",
        params: { start, end, user },
      }),
  },

  // Event Templates
  eventTemplates: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/event_templates/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/event_templates/search",
        data: params,
      }),
  },

  // Locations
  locations: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/locations/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/locations/search",
        data: params,
      }),
  },

  // Forms
  forms: {
    find: (division: string, workflow?: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/forms?division=${division}${workflow ? `&workflow=${workflow}` : ''}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/forms/${id}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/forms/search",
        data: params,
      }),
    request: (data: {
      form: string
      matter: string
      contact: string
      division: string
      sendEmail?: boolean
      sendText?: boolean
      finishTask?: string
    }) =>
      request<{ success: boolean; sentText?: boolean; sentEmail?: boolean; queued?: boolean }>({
        method: "POST",
        url: "/v1/forms/request",
        data,
      }),
  },

  // Form Answers (tracks form progress per contact)
  formAnswers: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/form_answers/${matterId}`,
      }),
    find: (formId: string, matterId: string, contactId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/form_answers/${formId}/${matterId}/${contactId}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/form_answers/search",
        data: params,
      }),
  },

  // Form Submissions (completed forms)
  formSubmissions: {
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/form_submissions/${matterId}/matter`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/form_submissions/${id}`,
      }),
    search: (params: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/form_submissions/search",
        data: params,
      }),
  },

  // Snake_case aliases for CollectionSearch compatibility
  // (CollectionSearch uses collection names like "event_templates" not "eventTemplates")
  event_templates: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/event_templates/${id}`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/event_templates/search",
        data: params,
      }),
  },

  // Tags
  tags: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/tags/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/tags/search",
        data: params,
      }),
  },

  // Lead Sources
  lead_sources: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/lead_sources/${id}`,
      }),
    search: (params: { searchString?: string; filter?: Record<string, any>; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/lead_sources/search",
        data: params,
      }),
  },

  // Outstanding Items
  outstandingItems: {
    findByMatter: (matterId: string, finished = false) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/outstanding_items/${matterId}/matter`,
        params: { finished },
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/outstanding_items/${id}`,
      }),
    search: (data: {
      filter: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/outstanding_items/search",
        data,
      }),
    finish: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/outstanding_items/${id}/finish`,
        data,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/outstanding_items",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/outstanding_items/${id}`,
        data,
      }),
    unfinished: (id: string, data?: any) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/outstanding_items/${id}/unfinished`,
        data,
      }),
    searchByMatter: (data: {
      filter: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/outstanding_items/search_by_matter",
        data,
      }),
  },

  // Outstanding Item Templates
  outstandingItemTemplates: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/outstanding_item_templates/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/outstanding_item_templates/${matterId}/matter`,
      }),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/outstanding_item_templates/search",
        data,
      }),
  },

  // General Items
  generalItems: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/general_items/${id}`,
      }),
    pending: (params?: { filter?: Record<string, any> }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/general_items/user/pending",
        params,
      }),
    search: (data: {
      filter: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/general_items/search",
        data,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/general_items",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/general_items/${id}`,
        data,
      }),
    finish: (id: string, data?: { outcome?: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/general_items/${id}/finish`,
        data,
      }),
    reopen: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/general_items/${id}/reopen`,
      }),
    findByContact: (contactId: string, finished?: boolean) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/general_items/contact/${contactId}`,
        params: finished !== undefined ? { finished: finished ? "true" : "false" } : undefined,
      }),
  },

  // Workflow Links
  workflowLinks: {
    find: (workflowId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/workflow_links`,
        params: { workflow: workflowId },
      }),
  },

  // Matter Approvals
  matterApprovals: {
    search: (params: {
      searchString?: string
      page?: number
      skip?: number
      sizePerPage?: number
      limit?: number
      filter?: Record<string, any>
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/matter_approvals/search",
        data: params,
      }),
    pending: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/pending",
      }),
    returned: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/returned",
      }),
    denied: () =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: "/v1/matter_approvals/denied",
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_approvals/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/matter_approvals/matter/${matterId}`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/matter_approvals",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any; message?: string[] }>({
        method: "PATCH",
        url: `/v1/matter_approvals/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_approvals/${id}`,
      }),
    addDocument: (id: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/matter_approvals/${id}/documents`,
        data: { document_id: documentId },
      }),
    removeDocument: (id: string, documentId: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/matter_approvals/${id}/documents/${documentId}`,
      }),
  },

  // Matter Approval Templates
  matterApprovalTemplates: {
    find: (params?: { workflow?: string; division?: string }) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/matter_approval_templates",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/matter_approval_templates/${id}`,
      }),
  },

  // Portal Client Requests
  portalClientRequests: {
    pending: (data?: { filter?: Record<string, unknown>; skip?: number; limit?: number }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/portal_client_requests/pending",
        data,
      }),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/portal_client_requests/search",
        data,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/portal_client_requests/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/portal_client_requests/matter/${matterId}`,
      }),
    review: (id: string, data: { status: "approved" | "denied"; admin_note?: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/portal_client_requests/${id}/review`,
        data,
      }),
  },

  // User Comments
  userComments: {
    find: (collection: string, id: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/user_comments/${collection}/${id}`,
      }),
    create: (data: {
      collection_name: string
      collection_id: string
      matter: string
      body: string
      tagged_users?: string[]
      parent_name?: string
    }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/user_comments",
        data: {
          ...data,
          tagged_users: data.tagged_users || [],
        },
      }),
    markSeen: (data: { collection_name: string; collection_id: string }) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/user_comments/seen",
        data,
      }),
    update: (id: string, data: { body: string; tagged_users?: string[]; parent_name?: string }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/user_comments/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "DELETE",
        url: `/v1/user_comments/${id}`,
      }),
    search: (params: Record<string, unknown>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/user_comments/search",
        data: params,
      }),
    getCount: (divisionId: string) =>
      request<{ data: number }>({
        method: "GET",
        url: `/v1/user_comments/count/${divisionId}`,
      }),
    markSeenById: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/user_comments/${id}/seen`,
      }),
    markUnseenById: (id: string) =>
      request<{ success: boolean }>({
        method: "POST",
        url: `/v1/user_comments/${id}/unseen`,
      }),
  },

  // AI Features
  ai: {
    documents: {
      merge: (data: { matter: string; name: string; prompt?: string; documents: string[] }) =>
        request<{ success: boolean; data?: any; message?: string[] }>({
          method: "POST",
          url: "/v1/ai/documents/merge",
          data,
          timeout: 120000,
        }),
    },
  },

  // AI Chat Threads
  aiChatThreads: {
    sendMessage: (data: {
      matter_id: string
      thread_id?: string
      content: string
      attachments?: Array<{
        id: string
        content: string
        mimetype: string
        filename: string
        size: number
      }>
    }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/ai_chat_threads",
        data,
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/ai_chat_threads/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/ai_chat_threads/${matterId}/matter`,
      }),
  },

  // AI Jobs
  aiJobs: {
    create: (data: {
      name: string
      description: string
      prompt: string
      division: string
      manual?: boolean
      [key: string]: any // For prompt_X and prompt_X_enabled fields
    }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/ai_jobs",
        data,
      }),
    run: (jobId: string, matterId: string) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/ai_jobs/${jobId}/${matterId}`,
      }),
    findByDivision: (divisionId: string, isSystemTask?: boolean) =>
      request<{ success: boolean; data: any[] }>({
        method: "GET",
        url: `/v1/ai_jobs/${divisionId}/division`,
        params: isSystemTask ? { isSystemTask: "true" } : undefined,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: `/v1/ai_jobs/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean }>({
        method: "DELETE",
        url: `/v1/ai_jobs/${id}`,
      }),
  },

  // Billing Categories
  billingCategories: {
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/billing_categories/${division}`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/billing_categories",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/billing_categories/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/billing_categories/${id}`,
      }),
    reorder: (data: Record<string, unknown>) =>
      request<{ success: boolean }>({
        method: "POST",
        url: "/v1/billing_categories/reorder",
        data,
      }),
  },

  // Time Entries
  timeEntries: {
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/time_entries/search",
        data: params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/time_entries/${id}`,
      }),
    findByMatter: (matterId: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/time_entries/${matterId}/matter`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "PATCH",
        url: `/v1/time_entries/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/time_entries/${id}`,
      }),
    approve: (data: { time_entry_ids: string[] }) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/approve",
        data,
      }),
    importToInvoice: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/import_to_invoice",
        data,
      }),
    batchInvoice: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/batch_invoice",
        data,
      }),
    removeFromInvoice: (data: { time_entry_ids: string[]; invoice_id: string }) =>
      request<{ success: boolean; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/remove_from_invoice",
        data,
      }),
    previewRate: (data: any) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/time_entries/preview_rate",
        data,
      }),
    timesheet: (params: { week_start_date: string; user?: string }) =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/time_entries/timesheet",
        params,
      }),
    mySummary: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/time_entries/my_summary",
      }),
    timerStart: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/timer/start",
        data,
      }),
    timerStop: () =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/timer/stop",
      }),
    timerActive: () =>
      request<{ data: any }>({
        method: "GET",
        url: "/v1/time_entries/timer/active",
      }),
    combineEntries: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entries/combine_entries",
        data,
      }),
  },

  // Time Entry Templates
  timeEntryTemplates: {
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/time_entry_templates/${division}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/time_entry_templates/${id}/detail`,
      }),
    search: (params: any) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/time_entry_templates/search",
        data: params,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/time_entry_templates",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/time_entry_templates/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/time_entry_templates/${id}`,
      }),
  },

  // Rate Cards
  rateCards: {
    find: (division: string) =>
      request<{ data: any[] }>({
        method: "GET",
        url: `/v1/rate_cards/${division}`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/rate_cards/${id}/detail`,
      }),
    create: (data: any) =>
      request<{ success: boolean; data?: any; message?: string[] }>({
        method: "POST",
        url: "/v1/rate_cards",
        data,
      }),
    update: (id: string, data: any) =>
      request<{ success: boolean; message?: string[] }>({
        method: "PATCH",
        url: `/v1/rate_cards/${id}`,
        data,
      }),
    delete: (id: string) =>
      request<{ success: boolean; message?: string[] }>({
        method: "DELETE",
        url: `/v1/rate_cards/${id}`,
      }),
  },

  // User Activity Tracking
  user_activity_slices: {
    save: (data: any) =>
      request<{ accepted: boolean }>({
        method: "POST",
        url: "/v1/user_activity_slices",
        data,
      }),
  },

  // Training Enrollments (user-facing)
  trainingEnrollments: {
    me: (params?: Record<string, any>) =>
      request<{ data: any[] }>({
        method: "GET",
        url: "/v1/training_enrollments/me",
        params,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_enrollments/${id}`,
      }),
    search: (data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/training_enrollments/search",
        data,
      }),
    submitQuiz: (id: string, data: { answers: { question_index: number; selected_answer: string }[]; started_at?: number }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/training_enrollments/${id}/submit_quiz`,
        data,
      }),
  },

  // Training Courses (read-only for users)
  trainingCourses: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_courses/${id}`,
      }),
  },

  // Training Units (read-only for users)
  trainingUnits: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_units/${id}`,
      }),
    search: (data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/training_units/search",
        data,
      }),
  },

  // Training Scenarios (read-only for users)
  trainingScenarios: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_scenarios/${id}`,
      }),
    search: (data: Record<string, unknown>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/training_scenarios/search",
        data,
      }),
  },

  // Training Simulations
  trainingSimulations: {
    start: (data: { scenario: string; enrollment?: string; course?: string; unit?: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/training_simulations/start",
        data,
      }),
    end: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/training_simulations/${id}/end`,
      }),
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_simulations/${id}`,
      }),
    search: (data: Record<string, unknown>) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/training_simulations/search",
        data,
      }),
    uploadRecording: (id: string, blob: Blob) => {
      const formData = new FormData()
      formData.append("recording", blob, "recording.webm")
      return request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/training_simulations/${id}/upload_recording`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
  },

  // Reference Data (geo lookups)
  reference: {
    geo: {
      counties: (state: string) =>
        request<{ success: boolean; data: string[] }>({
          method: "GET",
          url: "/v1/reference/geo/counties",
          params: { state },
        }),
      districtLookup: (state: string, county: string) =>
        request<{ success: boolean; data: { district: string; division: string } | null }>({
          method: "GET",
          url: "/v1/reference/geo/district-lookup",
          params: { state, county },
        }),
    },
  },

  trainingQuizzes: {
    findById: (id: string) =>
      request<{ data: any }>({
        method: "GET",
        url: `/v1/training_quizzes/${id}`,
      }),
  },

  // Training Unit Progress (user progress tracking)
  trainingUnitProgress: {
    search: (data: Record<string, unknown>) =>
      request<{ data: any }>({
        method: "POST",
        url: "/v1/training_unit_progress/search",
        data,
      }),
    markComplete: (data: { enrollment: string; unit: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/training_unit_progress/mark_complete",
        data,
      }),
    saveVideoProgress: (data: { enrollment: string; unit: string; video_position: number; video_watched_seconds: number }) =>
      request<{ success: boolean; data: any }>({
        method: "PATCH",
        url: "/v1/training_unit_progress/save_video_progress",
        data,
      }),
    submitQuiz: (data: { enrollment: string; unit: string; answers: { question_index: number; selected_answer: string }[]; started_at?: number }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: "/v1/training_unit_progress/submit_quiz",
        data,
      }),
  },

  billingAutomationInstances: {
    list: (params: Record<string, unknown>) =>
      request<{ data: any[]; total: number }>({
        method: "GET",
        url: "/v1/billing_automation_instances",
        params,
      }),
    resume: (id: string) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/resume`,
      }),
    resolve: (id: string) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/resolve`,
      }),
    logCall: (id: string, body: { advance: boolean }) =>
      request<{ success: boolean }>({
        method: "PUT",
        url: `/v1/billing_automation_instances/${id}/log_call`,
        data: body,
      }),
  },

  previewLinks: {
    create: (data: { matter: string }) =>
      request<{ success: boolean; data: { id: string } }>({
        method: "POST",
        url: "/v1/preview_links",
        data,
      }),
  },

  changelogEntries: {
    search: (data?: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      searchString?: string
      type?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/changelog_entries/search",
        data,
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/changelog_entries/${id}`,
      }),
    getUnreadCount: () =>
      request<{ success: boolean; data: { count: number } }>({
        method: "GET",
        url: "/v1/changelog_entries/unread_count",
      }),
    markViewed: () =>
      request<{ success: boolean; data: { marked: boolean } }>({
        method: "POST",
        url: "/v1/changelog_entries/mark_viewed",
      }),
  },
  systemTickets: {
    create: (data: Record<string, unknown>) =>
      request<{ success: boolean; data: { _id: string } }>({
        method: "POST",
        url: "/v1/system_tickets/create",
        data,
      }),
    upload: (formData: FormData) =>
      apiClient.post<{ success: boolean; data: unknown }>(
        "/v1/system_tickets/upload",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      ),
    search: (data: {
      filter?: Record<string, unknown>
      skip?: number
      limit?: number
      sort?: Record<string, number>
      searchString?: string
    }) =>
      request<{ data: { documents: any[]; total_documents: number } }>({
        method: "POST",
        url: "/v1/system_tickets/search",
        data,
      }),
    findById: (id: string) =>
      request<{ success: boolean; data: any }>({
        method: "GET",
        url: `/v1/system_tickets/${id}`,
      }),
    reopen: (id: string, data: { message: string }) =>
      request<{ success: boolean; data: any }>({
        method: "POST",
        url: `/v1/system_tickets/${id}/reopen`,
        data,
      }),
  },
}

export { apiClient }
